import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ImageCropper Component
 * Allows users to crop and adjust image positioning
 */
export default function ImageCropper({ 
  imageUrl, 
  onCropComplete, 
  onCancel,
  aspectRatio = 16/9 
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropChange = useCallback((newCrop) => {
    setCrop(newCrop);
  }, []);

  const onZoomChange = useCallback((newZoom) => {
    setZoom(newZoom);
  }, []);

  const onCropAreaComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (croppedAreaPixels && onCropComplete) {
      try {
        const croppedImage = await getCroppedImage(imageUrl, croppedAreaPixels);
        onCropComplete(croppedImage);
      } catch (e) {
        console.error('Error cropping image:', e);
        // If cropping fails, just use the original with crop data
        onCropComplete({
          url: imageUrl,
          cropData: { crop, zoom, croppedAreaPixels }
        });
      }
    }
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-zinc-900 border-b border-zinc-700">
        <h3 className="text-lg font-semibold text-white">Adjust Image</h3>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onCancel}
            className="text-zinc-400 hover:text-white"
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          >
            <Check className="h-4 w-4 mr-1" />
            Apply
          </Button>
        </div>
      </div>

      {/* Cropper Area */}
      <div className="flex-1 relative">
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={onCropAreaComplete}
          showGrid={true}
          style={{
            containerStyle: {
              background: '#18181b'
            },
            cropAreaStyle: {
              border: '2px solid #D4AF37'
            }
          }}
        />
      </div>

      {/* Controls */}
      <div className="p-4 bg-zinc-900 border-t border-zinc-700">
        <div className="max-w-md mx-auto space-y-4">
          {/* Zoom Control */}
          <div className="flex items-center gap-4">
            <ZoomOut className="h-4 w-4 text-zinc-400" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-navigate-gold"
            />
            <ZoomIn className="h-4 w-4 text-zinc-400" />
            <span className="text-sm text-zinc-400 w-12 text-right">{Math.round(zoom * 100)}%</span>
          </div>

          {/* Reset Button */}
          <div className="flex justify-center">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={resetCrop}
              className="text-zinc-400 border-zinc-600 hover:bg-zinc-800"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>

          {/* Instructions */}
          <p className="text-xs text-zinc-500 text-center">
            Drag to reposition • Scroll or use slider to zoom • Click Apply when done
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Creates a cropped image from the source
 */
async function getCroppedImage(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Set canvas size to the cropped area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({
            blob,
            url: URL.createObjectURL(blob),
            width: pixelCrop.width,
            height: pixelCrop.height
          });
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      'image/jpeg',
      0.9
    );
  });
}

/**
 * Creates an image element from a URL
 */
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

// Also export a simpler FocalPointSelector for quick adjustments
export function FocalPointSelector({ value = 'center', onChange }) {
  const positions = [
    { id: 'top-left', label: '↖' },
    { id: 'top', label: '↑' },
    { id: 'top-right', label: '↗' },
    { id: 'left', label: '←' },
    { id: 'center', label: '•' },
    { id: 'right', label: '→' },
    { id: 'bottom-left', label: '↙' },
    { id: 'bottom', label: '↓' },
    { id: 'bottom-right', label: '↘' },
  ];

  return (
    <div className="grid grid-cols-3 gap-1 w-24">
      {positions.map((pos) => (
        <button
          key={pos.id}
          onClick={() => onChange(pos.id)}
          className={`w-8 h-8 rounded text-sm flex items-center justify-center transition-colors ${
            value === pos.id
              ? 'bg-navigate-gold text-black'
              : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
          }`}
          title={pos.id.replace('-', ' ')}
        >
          {pos.label}
        </button>
      ))}
    </div>
  );
}

// CSS position mapping for focal point
export function getFocalPointStyle(position) {
  const positionMap = {
    'top-left': 'object-position: left top',
    'top': 'object-position: center top',
    'top-right': 'object-position: right top',
    'left': 'object-position: left center',
    'center': 'object-position: center center',
    'right': 'object-position: right center',
    'bottom-left': 'object-position: left bottom',
    'bottom': 'object-position: center bottom',
    'bottom-right': 'object-position: right bottom',
  };
  return positionMap[position] || positionMap['center'];
}
